import { DEFAULT_SWING_DURATION_MS } from '@common/services/CombatService';

import { ClientGlobalSystem } from './ClientBase';

/**
 * Client-side prediction for weapon swings.
 * Listens for local player attack input and immediately spawns a visual-only swing entity.
 */
export class ClientWeaponSwingSpawnSystem extends ClientGlobalSystem {
  private wasAttacking = false;

  run(): void {
    const world = this.getWorld();
    const localEntityId = this.clientServices.localPlayer.entityId;

    if (localEntityId === undefined) return;

    const input = world.getComponent(localEntityId, 'Input');
    const transform = world.getComponent(localEntityId, 'Transform');
    const facing = world.getComponent(localEntityId, 'Facing');

    if (!input || !transform || !facing) return;

    const isAttacking = !!input.attack;

    if (isAttacking && !this.wasAttacking) {
      // 1. Determine weapon stats
      const weaponStats = this.clientServices.common.combat.getWeaponStats(
        world,
        this.clientServices.equipment,
        localEntityId,
      );

      // 2. Spawn predicted swing entities
      const attackerHitbox = world.getComponent(localEntityId, 'Hitbox');
      const { y: centerY } = attackerHitbox
        ? this.clientServices.common.combat.getHitboxCenter(attackerHitbox)
        : { y: -16 }; // Fallback for humanoid-like sprites if hitbox is missing

      for (let i = 0; i < weaponStats.hitboxCount; i++) {
        const swingEntityId = world.createEntity();

        world.addComponent(
          swingEntityId,
          world.createComponent('WeaponSwing', {
            ownerEntityId: localEntityId,
            hitEntities: [],
            progress: 0,
            elapsedMs: 0,
            durationMs: DEFAULT_SWING_DURATION_MS,
            maxExtension: weaponStats.range,
            hitboxIndex: i,
          }),
        );

        world.addComponent(swingEntityId, world.createComponent('IsPredicted', {}));

        world.addComponent(
          swingEntityId,
          world.createComponent('Transform', {
            x: 0,
            y: centerY,
            rotation: 0,
            scaleX: 1,
            scaleY: 1,
            sceneId: transform.sceneId,
          }),
        );

        world.addComponent(
          swingEntityId,
          world.createComponent('Hitbox', {
            width: weaponStats.bladeWidth,
            height: weaponStats.bladeWidth,
            offsetX: -weaponStats.bladeWidth / 2,
            offsetY: -weaponStats.bladeWidth / 2,
          }),
        );

        // Add to hierarchy
        world.addChildren(localEntityId, [swingEntityId]);
      }
    }

    this.wasAttacking = isAttacking;
  }
}
